 
 
 
 
 
 
/**
 * Allows to make search request using the TomTom's
 * [Fuzzy Search API](https://developer.tomtom.com/online-search/online-search-documentation-search/fuzzy-search).
 *
 * Parameters can be passed to the constructor or provided via convenient methods that can be chained until
 * the method {{#crossLink "FuzzySearch/go:method"}}{{/crossLink}} performs the actual call.
 *
 * The call is asynchronous therefore the user have two options for receive the response:
 *  - Passing a callback function.
 *  - Use the {{#crossLink "Promise"}}{{/crossLink}} returned by the
 *    {{#crossLink "FuzzySearch/go:method"}}{{/crossLink}} method to handle the response.
 *
 * The list of all supported category codes are available
 * [here](https://developer.tomtom.com/online-search/online-search-documentation/supported-category-codes).
 *
 * @example
 * ```js
 * function callbackFn(result) {
 *   console.log(result);
 * };
 *
 * // Using a callback function
 * tomtom.fuzzySearch()
 *   .key(<Your API key>) // this is optional if you defined a global key earlier
 *   .query("Amsterdam")
 *   .callback(callbackFn)
 *   .go();
 *
 * // Or using a Promise
 * tomtom.fuzzySearch()
 *   .key(<Your API key>) // this is optional if you defined a global key earlier
 *   .query("Amsterdam")
 *   .go()
 *   .then(callbackFn);
 * ```
 *
 * The SDK provides a {{#crossLink "Promise"}}{{/crossLink}} polyfill for browsers (IE<9)
 * without native ECMAScript 6 support.
 *
 * @class FuzzySearch
 * @extends Search
 * @uses ExtendedPostalCodesForMixin
 * @uses CenterMixin
 * @uses RadiusMixin
 * @uses BoundingBoxMixin
 * @uses CountrySetMixin
 * @uses OffsetMixin
 * @uses BestResultMixin
 * @uses QueryMixin
 * @uses TypeaheadMixin
 * @constructor
 * @module Services
 *
 * @param {Object}   [options] Options to be passed to the fuzzy search call,
 *     or Array of such options objects to perform batch request.
 * @param {String}   [options.key] An API Key issued from <a href="http://developer.tomtom.com">Developer Portal</a>
 * @param {"http"|"https"} [options.protocol="https"] Protocol's type
 * @param {Boolean}  [options.unwrapBbox=false] Changes the bounding box coordinates allowing longitudes that
 *  exceeds 180 degrees when is necessary. {{#crossLink "Search/unwrapBbox:method"}}More{{/crossLink}}
 * @param {Number}   [options.limit=10] Maximum number of responses that will be returned
 * @param {String}   [options.language=null] Language to use in the response.
 *  The list with all the available codes can be found
 *  <a href="https://developer.tomtom.com/online-search/online-search-documentation/supported-languages">here</a>.
 * @param {Function} [options.callback] Callback function to use in case of success
 * @param {Function} [options.fail=default] Callback function to use in case of failure
 *
 * @param {String}   [options.query] Text that will be searched
 *  This parameter is required to be defined either in the options passed to the constructor or using the
 *  **query()** method.
 * @param {Boolean}  [options.typeahead=false] If is enabled the query will be interpreted as a
 *  partial input and the **predictive** mode will be used
 * @param {Boolean}  [options.bestResult=false] Return only one result, the best match result.
 * @param {Number[]|String|L.LatLng|Object} [options.center] Location where results should be biased
 * @param {Number}   [options.radius=0] The search radius in meters using the coordinates given to the **center**
 *  option as origin.
 *  If the radius is zero the results will not be constrained.
 * @param {Number}   [options.offset=0] Starting offset of the returned results within the full result set
 * @param {String}   [options.countrySet] List of country codes for limit the search request to those specific
 *  political limits
 * @param {Object|Number[]|Object[]|String} [options.boundingBox] The bounding box is an area within the search
 *  results needs to limit to. If it is omitted then the whole world will be taken into consideration.
 * @param {String}   [options.extendedPostalCodesFor] Represent the indexes for which extended postal codes should
 *  be included in the results
 * @param {String}   [options.idxSet] Specify which indexes to utilize for the search
 * @param {Number}   [options.maxFuzzyLevel=1] Maximum fuzzyness level to be used
 */
define(['./search', '../validators'], function(search, validators) {
    'use strict';

    var SearchPrototype = search.prototype.constructor;

    function FuzzySearch(options) {
        SearchPrototype.call(this, options);

        this.fields.type = {
            defaultValue: this.TYPE.search,
            visible: false
        };

        /**
         * Sets or gets the current value of the option "maxFuzzyLevel".
         *
         * The maximum allowed value is 3.
         *
         * @method maxFuzzyLevel
         * @chainable
         * @param {Number} [newValue] The possible values are the integers 1, 2 and 3
         * @throws {TypeError} If the given argument cannot be converted to a valid value
         * @return {Number} The same service instance or the current option value if no argument was given
         */
        this.fields.maxFuzzyLevel = {
            validator: validators.fuzzinessLevel,
            defaultValue: 1
        };

        this.fields.idxSet = {
            validator: validators.string
        };
    }

    FuzzySearch.prototype = new SearchPrototype();
    FuzzySearch.prototype.constructor = FuzzySearch;

    return function(options) {
        return (new FuzzySearch(options)).construct();
    };
});



// WEBPACK FOOTER //
// ./services/search/fuzzySearch.js